import { onboardingGiftLabel, resolveGiftedDays } from "@/lib/onboarding-gift";
import { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Logo from "@/components/shared/Logo";
import StickyBar from "@/components/shared/StickyBar";
import HelpTooltip from "@/components/shared/HelpTooltip";
import ProductUpdatesPreference from "@/components/onboarding/ProductUpdatesPreference";
import SummaryPreferencesSection from "@/components/onboarding/SummaryPreferencesSection";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { listAccountChats } from "@/lib/api";
import {
  canSavePreferencesForPlan,
  getRequiredPlanForPreferences,
  hasUnsavedPreferenceChanges,
  normalizeChatRules,
  preferencesToApi,
} from "@/lib/preferences";
import { useI18n } from "@/lib/i18n";
import {
  buildAlphabeticalLanguageSelectOptions,
  buildLanguageQuickPickOptions,
} from "@/lib/languages";
import { formatPlanRequirementCopy, getPlanLabel, planIndex } from "@/lib/plans";

const DEFAULT_ROWS = [
  {
    label: "Incoming voice notes",
    tooltip: "When someone sends you a voice note, Min DMs you the result on WhatsApp.",
    automaticKey: "inboundAutomatic",
    summaryKey: "inboundSummary",
    transcriptKey: "inboundTranscript",
  },
  {
    label: "Outgoing voice notes",
    tooltip: "When you send a voice note, Min sends a follow-up after your message.",
    automaticKey: "outboundAutomatic",
    summaryKey: "outboundSummary",
    transcriptKey: "outboundTranscript",
  },
];

const CHAT_RULE_OPTIONS = [
  { value: "inherit", label: "Inherit" },
  { value: "off", label: "Off" },
  { value: "summary", label: "Summary" },
  { value: "transcript", label: "Transcript" },
  { value: "both", label: "Both" },
];

const BRANDING_OPTIONS = [
  { value: "inherit", label: "Inherit" },
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

const DELIVERY_LANGUAGE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "source_only", label: "Source only" },
  { value: "dual_summary", label: "Dual summary" },
  { value: "translate_all", label: "Translate all" },
];

const INBOUND_OWNER_COPY_OPTIONS = [
  { value: "always", label: "Always", tooltip: "Send you a copy for every incoming voice note." },
  { value: "no_chat_summary", label: "No duplicate", tooltip: "Skip your copy when the chat already gets a Min summary." },
  { value: "different_outputs", label: "Extra details", tooltip: "Send your copy only when it adds more detail." },
];

function isPlanLocked(requiredPlan, effectivePlan) {
  return planIndex(requiredPlan) > planIndex(effectivePlan);
}

function updatePreference(setPreferences, key, value) {
  setPreferences((current) => ({ ...current, [key]: value }));
}

function updateChatRule(setPreferences, chatId, key, value) {
  setPreferences((current) => ({
    ...current,
    chatRules: current.chatRules.map((rule) => (
      rule.chatId === chatId
        ? { ...rule, [key]: value }
        : rule
    )),
  }));
}

function addChatRule(setPreferences, chatId) {
  setPreferences((current) => {
    if (current.chatRules.some((rule) => rule.chatId === chatId)) {
      return current;
    }
    return {
      ...current,
      chatRules: [
        {
          chatId,
          inboundMode: "inherit",
          outboundMode: "inherit",
          brandingMode: "inherit",
          targetLanguageCode: "",
          deliveryLanguageMode: "auto",
        },
        ...current.chatRules,
      ],
    };
  });
}

function removeChatRule(setPreferences, chatId) {
  setPreferences((current) => ({
    ...current,
    chatRules: current.chatRules.filter((rule) => rule.chatId !== chatId),
  }));
}

function createEmptyChatMeta() {
  return {
    visibleRecentCount: 0,
    selectedCount: 0,
    cachedCount: 0,
    syncing: false,
    query: "",
  };
}

function preferenceSignature(preferences) {
  const normalized = preferencesToApi(preferences);
  return JSON.stringify({
    ...normalized,
    chatRules: normalized.chatRules
      .map((rule) => JSON.stringify(rule))
      .sort(),
  });
}

function mergeChatsById(...collections) {
  const merged = new Map();
  collections.flat().forEach((chat) => {
    if (!chat?.id) {
      return;
    }
    merged.set(chat.id, chat);
  });
  return [...merged.values()];
}

function TableLabelWithTooltip({ label, tooltip, mobileClassName = "" }) {
  const { tx } = useI18n();

  return (
    <div className="flex items-center gap-2">
      <HelpTooltip
        content={tx(tooltip)}
        mobileLabel={tx(label)}
        showLabelOnDesktop
        labelClassName={mobileClassName}
      />
    </div>
  );
}

function SegmentedTooltipItem({ option, selected, className }) {
  const { tx } = useI18n();

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleGroupItem
            value={option.value}
            aria-label={tx(option.label)}
            title={tx(option.tooltip)}
            data-selected={selected ? "true" : undefined}
            className={className}
          >
            {tx(option.label)}
          </ToggleGroupItem>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-56 border border-border/80 bg-background px-3 py-2 text-[13px] text-foreground shadow-xl shadow-black/10">
          {tx(option.tooltip)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function selectLabel(options, value) {
  return options.find((option) => option.value === value)?.label || options[0]?.label || "";
}

function normalizeChatLanguageSelectValue(value) {
  const rawValue = String(value || "").trim();
  if (rawValue === "__none__") {
    return "";
  }
  if (rawValue.startsWith("language:")) {
    return rawValue.slice("language:".length);
  }
  return rawValue;
}

function ChatRuleSelect({ value, options, onValueChange, ariaLabel }) {
  const { tx } = useI18n();

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label={tx(ariaLabel)} className="h-10 rounded-xl border-border bg-background text-sm">
        <SelectValue>{tx(selectLabel(options, value))}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          option.type === "separator" ? (
            <SelectSeparator key={option.key} />
          ) : (
            <SelectItem key={option.value} value={option.value}>
              {tx(option.label)}
            </SelectItem>
          )
        ))}
      </SelectContent>
    </Select>
  );
}

function AddChatPopover({
  chats,
  search,
  onSearchChange,
  loading,
  onAdd,
  emptyLabel,
  open,
  onOpenChange,
}) {
  const { tx } = useI18n();

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-10 rounded-xl border-border px-4 text-sm font-medium">
          <Plus className="mr-2 h-4 w-4" />
          {tx("Add chat")}
          {loading ? <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" /> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(26rem,calc(100vw-2rem))] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={tx("Search chats...")}
            value={search}
            onValueChange={onSearchChange}
            showClear={Boolean(search)}
            onClear={() => onSearchChange("")}
          />
          <CommandList>
            <CommandEmpty>{tx(emptyLabel)}</CommandEmpty>
            {chats.map((chat) => (
              <CommandItem
                key={chat.id}
                value={`${chat.name} ${chat.kind} ${chat.phoneNumber || ""}`}
                onSelect={() => {
                  onAdd(chat.id);
                  onOpenChange(false);
                }}
                className="items-start gap-3 px-3 py-3"
              >
                <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Plus className="h-3 w-3" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{chat.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {chat.kind === "group"
                      ? tx("Group chat")
                      : chat.phoneNumber
                        ? <span>{chat.phoneNumber}</span>
                        : tx("Direct chat")}
                  </span>
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ChatRuleName({ chatId, chatsById }) {
  const { tx } = useI18n();
  const chat = chatsById.get(chatId);
  const name = chat?.name || chatId;
  const secondary = chat?.kind === "group" ? "Group chat" : (chat?.phoneNumber || "Direct chat");

  return (
    <div className="min-w-0">
      <div className="truncate text-sm font-semibold text-foreground">{name}</div>
      <div className="truncate text-xs text-muted-foreground">
        {secondary === "Group chat" || secondary === "Direct chat" ? tx(secondary) : <span>{secondary}</span>}
      </div>
    </div>
  );
}

function RemoveChatRuleButton({ chatId, onRemove, className = "h-10 w-10 rounded-xl text-muted-foreground" }) {
  const { tx } = useI18n();

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={className}
            onClick={onRemove}
            aria-label={`${tx("Remove")} ${chatId}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="border border-border/80 bg-background px-3 py-2 text-[13px] text-foreground shadow-xl shadow-black/10">
          {tx("Remove row")}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function PreferencesSetup({
  accountId,
  effectivePlan,
  initialPreferences,
  savedPreferences = initialPreferences,
  isOnboarding = false,
  giftedMonths = 0,
  giftedDays,
  referrerName = "",
  embedded = false,
  onSave,
  onUpgrade,
}) {
  const { tx } = useI18n();
  const resolvedGiftedDays = resolveGiftedDays({ giftedDays, giftedMonths });
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(initialPreferences);
  const [chats, setChats] = useState([]);
  const [chatMeta, setChatMeta] = useState(createEmptyChatMeta());
  const [chatSearch, setChatSearch] = useState("");
  const [debouncedChatSearch, setDebouncedChatSearch] = useState("");
  const [chatPopoverOpen, setChatPopoverOpen] = useState(false);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsError, setChatsError] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const celebrationKeyRef = useRef("");

  const minEnabled = preferences.minEnabled !== false;
  const hasUnsavedChanges = hasUnsavedPreferenceChanges(preferences, savedPreferences);
  const showStickyBar = isOnboarding || hasUnsavedChanges;
  const requiredPlan = getRequiredPlanForPreferences(preferences);
  const canSave = canSavePreferencesForPlan(preferences, effectivePlan);
  const outboundSummaryLocked = isPlanLocked("fun", effectivePlan) && preferences.outboundSummary === false;
  const inboundSummaryLocked = isPlanLocked("fun", effectivePlan) && preferences.inboundSummary;
  const transcriptLocked = isPlanLocked("fun", effectivePlan)
    && (preferences.inboundTranscript || preferences.outboundTranscript);
  const automaticLocked = isPlanLocked("fun", effectivePlan)
    && (!preferences.inboundAutomatic || !preferences.outboundAutomatic);
  const brandingLocked = isPlanLocked("pro", effectivePlan) && preferences.branding === false;
  const groupRulesLocked = isPlanLocked("fun", effectivePlan) && preferences.groupChats !== true;
  const effectiveChatRules = normalizeChatRules(preferences.chatRules);
  const chatRulesLocked = isPlanLocked("pro", effectivePlan) && effectiveChatRules.some((rule) => (
    rule.inboundMode !== "inherit"
    || rule.outboundMode !== "inherit"
    || rule.brandingMode !== "inherit"
  ));
  const canManageChatRules = minEnabled;
  const isPending = pendingAction !== "";
  const funPlanRequirementCopy = tx(formatPlanRequirementCopy("fun"));
  const proPlanRequirementCopy = tx(formatPlanRequirementCopy("pro"));
  const chatRuleIds = preferences.chatRules.map((rule) => rule.chatId);
  const chatRuleIdSignature = chatRuleIds.join("\n");
  const usedChatLanguageCodeSignature = effectiveChatRules
    .map((rule) => rule.targetLanguageCode)
    .filter(Boolean)
    .join("\n");
  const languageQuickPickOptions = useMemo(
    () => buildLanguageQuickPickOptions({
      usedLanguageCodes: usedChatLanguageCodeSignature.split("\n").filter(Boolean),
      uiOnly: false,
    }),
    [usedChatLanguageCodeSignature],
  );
  const alphabeticalLanguageOptions = useMemo(
    () => buildAlphabeticalLanguageSelectOptions({ uiOnly: false }),
    [],
  );
  const chatLanguageOptions = useMemo(
    () => [
      { value: "__none__", label: "Auto" },
      ...languageQuickPickOptions.map((language) => ({
        value: language.code,
        label: language.label,
      })),
      { type: "separator", key: "language-list-separator" },
      ...alphabeticalLanguageOptions.map((language) => ({
        value: `language:${language.code}`,
        label: language.label,
      })),
    ],
    [alphabeticalLanguageOptions, languageQuickPickOptions],
  );
  const normalizedChatSearch = chatSearch.trim();
  const normalizedDebouncedChatSearch = debouncedChatSearch.trim();
  const hasShortChatSearch = normalizedChatSearch.length > 0 && normalizedChatSearch.length < 2;
  const initialPreferenceSignature = useMemo(
    () => preferenceSignature(initialPreferences),
    [initialPreferences],
  );
  const chatsById = useMemo(
    () => new Map(chats.map((chat) => [chat.id, chat])),
    [chats],
  );
  const pinnedChats = chats.filter((chat) => chatRuleIds.includes(chat.id));
  const otherChats = chats.filter((chat) => !chatRuleIds.includes(chat.id));
  const visibleChats = hasShortChatSearch ? pinnedChats : [...pinnedChats, ...otherChats];
  const addableChats = visibleChats.filter((chat) => !chatRuleIds.includes(chat.id));
  const chatEmptyLabel = chatsError
    || (hasShortChatSearch
      ? "Type at least 2 characters to search."
      : (chatsLoading || chatMeta.syncing
        ? "Loading chats..."
        : "No chats found."));

  useEffect(() => {
    setPreferences(initialPreferences);
  }, [accountId, initialPreferenceSignature]);

  useEffect(() => {
    setChats([]);
    setChatSearch("");
    setDebouncedChatSearch("");
    setChatPopoverOpen(false);
    setChatMeta(createEmptyChatMeta());
  }, [accountId]);

  useEffect(() => {
    if (!isOnboarding || resolvedGiftedDays <= 0 || typeof window === "undefined") {
      return;
    }
    const celebrationKey = `${accountId}:${resolvedGiftedDays}`;
    if (celebrationKeyRef.current === celebrationKey) {
      return;
    }
    celebrationKeyRef.current = celebrationKey;
    confetti({
      particleCount: 110,
      spread: 84,
      startVelocity: 28,
      scalar: 0.9,
      origin: { y: 0.18 },
    });
    window.setTimeout(() => {
      confetti({
        particleCount: 70,
        spread: 64,
        startVelocity: 22,
        scalar: 0.8,
        origin: { y: 0.12, x: 0.82 },
      });
    }, 180);
  }, [accountId, resolvedGiftedDays, isOnboarding]);

  useEffect(() => {
    if (!canManageChatRules) {
      setChats([]);
      setChatsLoading(false);
      setChatsError("");
      setChatPopoverOpen(false);
      setChatMeta(createEmptyChatMeta());
      return undefined;
    }
    if (!chatPopoverOpen || typeof window === "undefined") {
      setDebouncedChatSearch(chatSearch);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setDebouncedChatSearch(chatSearch);
    }, 225);
    return () => window.clearTimeout(timeoutId);
  }, [chatSearch, chatPopoverOpen, canManageChatRules]);

  useEffect(() => {
    let cancelled = false;

    async function loadSelectedChats() {
      if (!canManageChatRules) {
        return;
      }
      if (chatRuleIds.length === 0) {
        if (!cancelled) {
          setChatMeta((currentMeta) => ({
            ...currentMeta,
            selectedCount: 0,
          }));
        }
        return;
      }
      try {
        const payload = await listAccountChats(accountId, {
          limit: Math.max(chatRuleIds.length, 1),
          includeIds: chatRuleIds,
          selectedOnly: true,
        });
        if (!cancelled) {
          setChats((currentChats) => {
            const nonSelectedChats = currentChats.filter((chat) => !chatRuleIds.includes(chat.id));
            return mergeChatsById(payload.chats, nonSelectedChats);
          });
          setChatMeta({
            ...createEmptyChatMeta(),
            selectedCount: payload?.meta?.selectedCount ?? chatRuleIds.length,
            cachedCount: payload?.meta?.cachedCount ?? 0,
            ...(payload.meta || {}),
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Failed to load selected account chats", error);
        }
      }
    }

    void loadSelectedChats();

    return () => {
      cancelled = true;
    };
  }, [accountId, chatRuleIdSignature, canManageChatRules]);

  useEffect(() => {
    let cancelled = false;

    async function loadDirectoryChats() {
      if (!canManageChatRules || !chatPopoverOpen) {
        return;
      }
      if (normalizedDebouncedChatSearch.length > 0 && normalizedDebouncedChatSearch.length < 2) {
        setChatsLoading(false);
        setChatsError("");
        return;
      }
      try {
        setChatsLoading(true);
        setChatsError("");
        const payload = await listAccountChats(accountId, {
          limit: 100,
          includeIds: chatRuleIds,
          query: normalizedDebouncedChatSearch,
        });
        if (!cancelled) {
          setChats(payload.chats);
          setChatMeta({
            ...createEmptyChatMeta(),
            ...(payload.meta || {}),
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Failed to load account chats", error);
          setChatsError("Couldn’t load chats right now.");
        }
      } finally {
        if (!cancelled) {
          setChatsLoading(false);
        }
      }
    }

    void loadDirectoryChats();

    return () => {
      cancelled = true;
    };
  }, [accountId, chatRuleIdSignature, normalizedDebouncedChatSearch, chatPopoverOpen, canManageChatRules]);

  useEffect(() => {
    if (
      !canManageChatRules
      || !chatPopoverOpen
      || !chatMeta.syncing
      || typeof window === "undefined"
      || (normalizedDebouncedChatSearch.length > 0 && normalizedDebouncedChatSearch.length < 2)
    ) {
      return undefined;
    }
    const timeoutId = window.setTimeout(async () => {
      try {
        const payload = await listAccountChats(accountId, {
          limit: 100,
          includeIds: chatRuleIds,
          query: normalizedDebouncedChatSearch,
        });
        setChats(payload.chats);
        setChatMeta({
          ...createEmptyChatMeta(),
          ...(payload.meta || {}),
        });
      } catch {
        // Keep background indexing quiet in the picker.
      }
    }, 2000);
    return () => window.clearTimeout(timeoutId);
  }, [accountId, chatRuleIds, chatMeta.syncing, normalizedDebouncedChatSearch, chatPopoverOpen, canManageChatRules]);

  function handleChatPopoverOpenChange(nextOpen) {
    setChatPopoverOpen(nextOpen);
    if (!nextOpen) {
      setChatSearch("");
      setDebouncedChatSearch("");
      setChatsError("");
    }
  }

  async function saveCurrentPreferences() {
    setPendingAction("save");
    try {
      await Promise.resolve(onSave?.(preferences));
    } finally {
      setPendingAction("");
    }
  }

  async function handlePrimaryAction() {
    if (isPending) {
      return;
    }
    if (canSave) {
      await saveCurrentPreferences();
      return;
    }

    setPendingAction("upgrade");
    try {
      await Promise.resolve(onUpgrade?.(requiredPlan, preferences));
    } finally {
      setPendingAction("");
    }
  }

  return (
    <div className={embedded ? "flex flex-col" : "min-h-[100dvh] flex flex-col bg-background"}>
      {embedded ? null : (
        <div className="flex items-center justify-between px-5 py-4">
          <Logo size="sm" />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 rounded-xl px-3 text-muted-foreground"
              onClick={() => navigate("/account")}
            >
              {tx("Account")}
            </Button>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {tx(getPlanLabel(effectivePlan))}
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? "flex-1 pb-32 md:pb-28" : "flex-1 px-3 pb-32 sm:px-4 md:px-5 md:pb-28"}>
        <div className={embedded ? "flex flex-col gap-6" : "mx-auto flex w-full max-w-7xl flex-col gap-6"}>
          {isOnboarding && resolvedGiftedDays > 0 ? (
            <section
              className="rounded-[1.75rem] border border-primary/20 bg-primary/5 px-5 py-5 shadow-[0_18px_50px_-36px_rgba(14,165,233,0.85)] md:px-6"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                🎉 {tx("Free Fun unlocked")}
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                {tx(onboardingGiftLabel({ giftedDays, giftedMonths }))}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground md:text-base">
                {referrerName && resolvedGiftedDays === 38
                  ? tx("You both get a week")
                  : tx("Keep Fun for free now, then upgrade only if you want more control later.")}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {tx("A week when they join. A month of their plan when they first pay.")}
              </p>
            </section>
          ) : null}

          <motion.div
            className={embedded ? "max-[559px]:sr-only" : ""}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-display text-3xl font-bold md:text-4xl">{tx("Choose your defaults")}</h1>
          </motion.div>

          <section className="rounded-2xl border border-border bg-card px-4 py-3 md:px-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2">
                <h2 className="text-base font-semibold text-foreground">{tx("Use Min")}</h2>
                <HelpTooltip content={tx("Turn off summaries and transcripts without unlinking WhatsApp.")} />
              </div>
              <Switch
                checked={minEnabled}
                onCheckedChange={(checked) => updatePreference(setPreferences, "minEnabled", checked)}
                aria-label={tx("Use Min")}
              />
            </div>
          </section>

          {minEnabled ? (
            <div className="grid gap-6 md:grid-cols-2 md:items-start">
              <div className="space-y-6">
              <section className="rounded-[1.75rem] border border-border bg-card p-5 md:p-6">
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-2xl font-bold">{tx("Voice note defaults")}</h2>
                  <HelpTooltip content={tx("Choose whether Min sends a short summary or the full transcript after someone sends you a voice note, or after you send one.")} />
                </div>

                <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-border">
                  <table className="w-full table-fixed border-collapse text-left">
                    <colgroup>
                      <col className="w-[34%] sm:w-[37%]" />
                      <col className="w-[22%] sm:w-[21%]" />
                      <col className="w-[22%] sm:w-[21%]" />
                      <col className="w-[22%] sm:w-[21%]" />
                    </colgroup>
                    <thead>
                      <tr className="border-b border-border bg-muted/40">
                        <th className="px-3 py-3 text-xs font-semibold text-muted-foreground sm:px-4 sm:py-4 sm:text-sm md:px-5">
                          <TableLabelWithTooltip
                            label="Voice notes"
                            tooltip="Top row: someone sends you a voice note and Min DMs you the result. Bottom row: you send a voice note and Min sends a follow-up after it."
                          />
                        </th>
                        <th className="px-2 py-3 text-center sm:px-4 sm:py-4 md:px-5">
                          <div className="flex items-center justify-center gap-1 text-xs font-semibold text-foreground sm:gap-2 sm:text-sm">
                            <TableLabelWithTooltip
                              label="Automatic"
                              tooltip="Sends results without waiting for Ⓜ️. Turn this off if you want to approve that direction first."
                              mobileClassName="text-[10px] sm:text-sm"
                            />
                          </div>
                        </th>
                        <th className="px-2 py-3 text-center sm:px-4 sm:py-4 md:px-5">
                          <div className="flex items-center justify-center gap-1 text-xs font-semibold text-foreground sm:gap-2 sm:text-sm">
                            <TableLabelWithTooltip
                              label="Summary"
                              tooltip="Sends the short written version so you can skim the voice note quickly."
                              mobileClassName="text-[11px] sm:text-sm"
                            />
                          </div>
                        </th>
                        <th className="px-2 py-3 text-center sm:px-4 sm:py-4 md:px-5">
                          <div className="flex items-center justify-center gap-1 text-xs font-semibold text-foreground sm:gap-2 sm:text-sm">
                            <TableLabelWithTooltip
                              label="Transcript"
                              tooltip="Sends the full written version so you can read exactly what was said."
                              mobileClassName="text-[10px] sm:text-sm"
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {DEFAULT_ROWS.map((row, index) => (
                        <tr
                          key={row.label}
                          className={index === DEFAULT_ROWS.length - 1 ? "" : "border-b border-border"}
                        >
                          <td className="px-3 py-3 text-xs font-semibold text-foreground sm:px-4 sm:py-4 sm:text-sm md:px-5">
                            <TableLabelWithTooltip
                              label={row.label}
                              tooltip={row.tooltip}
                              mobileClassName="leading-snug"
                            />
                          </td>
                          <td className="px-2 py-3 text-center sm:px-4 sm:py-4 md:px-5">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={Boolean(preferences[row.automaticKey])}
                                onCheckedChange={(checked) => {
                                  updatePreference(setPreferences, row.automaticKey, checked === true);
                                }}
                                className="h-5 w-5 rounded-md"
                              />
                            </div>
                          </td>
                          <td className="px-2 py-3 text-center sm:px-4 sm:py-4 md:px-5">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={Boolean(preferences[row.summaryKey])}
                                onCheckedChange={(checked) => {
                                  updatePreference(setPreferences, row.summaryKey, checked === true);
                                }}
                                className="h-5 w-5 rounded-md"
                              />
                            </div>
                          </td>
                          <td className="px-2 py-3 text-center sm:px-4 sm:py-4 md:px-5">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={Boolean(preferences[row.transcriptKey])}
                                onCheckedChange={(checked) => {
                                  updatePreference(setPreferences, row.transcriptKey, checked === true);
                                }}
                                className="h-5 w-5 rounded-md"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {automaticLocked ? (
                  <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
                    {tx("Approving voice notes with Ⓜ️ needs")} {funPlanRequirementCopy}.
                  </div>
                ) : null}

                {inboundSummaryLocked ? (
                  <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
                    {tx("Incoming summaries need")} {funPlanRequirementCopy}.
                  </div>
                ) : null}

                {outboundSummaryLocked ? (
                  <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
                    {tx("Changing your outbound summary default needs")} {funPlanRequirementCopy}.
                  </div>
                ) : null}

                {transcriptLocked ? (
                  <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
                    {tx("Full transcripts need")} {funPlanRequirementCopy}.
                  </div>
                ) : null}

              </section>

              <section className="rounded-[1.75rem] border border-border bg-card p-5 md:p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-2">
                    <h2 className="font-display text-2xl font-bold">{tx("Min branding")}</h2>
                    <p className="max-w-2xl text-sm text-muted-foreground">
                      {tx("Keeps Min's footer and marker on the messages it sends for you.")}
                    </p>
                  </div>
                  <Switch
                    checked={preferences.branding}
                    onCheckedChange={(checked) => updatePreference(setPreferences, "branding", checked)}
                  />
                </div>

                {brandingLocked ? (
                  <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
                    {tx("Turning branding off needs")} {proPlanRequirementCopy}.
                  </div>
                ) : null}
              </section>

              {!isOnboarding ? (
                <ProductUpdatesPreference preferences={preferences} setPreferences={setPreferences} updatePreference={updatePreference} tx={tx} />
              ) : null}
              </div>

              <section className="rounded-[1.75rem] border border-border bg-card p-5 md:p-6">
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-2xl font-bold">{tx("Chat rules")}</h2>
                  <HelpTooltip content={tx("Add a chat when you want a different delivery language there, or when its inbound, outbound, or branding behavior should differ from your global defaults.")} />
                </div>

                <div className="mt-4 space-y-3 rounded-2xl bg-muted/30 px-4 py-3">
                  <div className="text-sm font-semibold text-foreground">{tx("Incoming voice notes")}</div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                      <span>{tx("Send copy")}</span>
                      <HelpTooltip content={tx("No duplicate skips your copy when the original chat already gets a Min summary. Extra details still sends when your copy adds something like a transcript.")} />
                    </div>
                    <ToggleGroup
                      type="single"
                      value={preferences.inboundOwnerCopyMode}
                      onValueChange={(value) => {
                        if (value) {
                          updatePreference(setPreferences, "inboundOwnerCopyMode", value);
                        }
                      }}
                      aria-label={tx("Send copy")}
                      className="grid w-full grid-cols-3 rounded-lg border border-border bg-background p-1 shadow-inner shadow-black/5"
                    >
                      {INBOUND_OWNER_COPY_OPTIONS.map((option) => (
                        <SegmentedTooltipItem
                          key={option.value}
                          option={option}
                          selected={preferences.inboundOwnerCopyMode === option.value}
                          className="h-10 rounded-md px-2 text-xs font-medium text-muted-foreground data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground data-[selected=true]:shadow-sm sm:px-3 sm:text-sm"
                        />
                      ))}
                    </ToggleGroup>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl bg-muted/30 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{tx("Use Min in group chats")}</span>
                    <HelpTooltip content={tx("Turn this off if Min should ignore groups unless you add a specific group row below.")} />
                  </div>
                  <Switch
                    checked={preferences.groupChats}
                    onCheckedChange={(checked) => updatePreference(setPreferences, "groupChats", checked)}
                  />
                </div>
                {/* Min Inbox reaction and reply controls are paused pending production validation; stored preferences and backend runtime compatibility remain. */}
                {groupRulesLocked ? (
                  <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
                    {tx("Group chat rules need")} {funPlanRequirementCopy}.
                  </div>
                ) : null}
              </section>

              <SummaryPreferencesSection preferences={preferences} setPreferences={setPreferences} effectivePlan={effectivePlan} funPlanRequirementCopy={funPlanRequirementCopy} proPlanRequirementCopy={proPlanRequirementCopy} />

              <section className="rounded-[1.75rem] border border-border bg-card p-5 md:col-span-2 md:p-6">
                <div className="space-y-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1">
                      <div className="text-sm font-semibold text-foreground">{tx("Per-chat overrides")}</div>
                      <p className="max-w-4xl text-sm text-muted-foreground">
                        {tx("Every plan can set a chat language. Pro also unlocks inbound, outbound, and branding overrides here.")}
                      </p>
                    </div>
                    <AddChatPopover
                      chats={addableChats}
                      search={chatSearch}
                      onSearchChange={setChatSearch}
                      loading={chatsLoading || chatMeta.syncing}
                      onAdd={(chatId) => addChatRule(setPreferences, chatId)}
                      emptyLabel={chatEmptyLabel}
                      open={chatPopoverOpen}
                      onOpenChange={handleChatPopoverOpenChange}
                    />
                  </div>

                  {chatRulesLocked ? (
                    <div className="rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
                      {tx("Inbound, outbound, and branding chat overrides need")} {proPlanRequirementCopy}. {tx("Language overrides stay available on every plan.")}
                    </div>
                  ) : null}

                  {preferences.chatRules.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
                      {tx("No chats added yet. Add a chat when its language or delivery behavior should differ from your global defaults.")}
                    </div>
                  ) : (
                    <>
                      <div className="hidden overflow-x-auto rounded-[1.5rem] border border-border md:block">
                        <table className="w-full min-w-[68rem] table-fixed border-collapse text-left">
                          <colgroup>
                            <col className="w-[25%]" />
                            <col className="w-[16%]" />
                            <col className="w-[16%]" />
                            <col className="w-[14%]" />
                            <col className="w-[14%]" />
                            <col className="w-[10%]" />
                            <col className="w-[5%]" />
                          </colgroup>
                          <thead>
                            <tr className="border-b border-border bg-muted/40">
                              <th className="px-4 py-3 text-sm font-semibold text-muted-foreground">{tx("Chat")}</th>
                              <th className="px-3 py-3 text-sm font-semibold text-muted-foreground">{tx("Language")}</th>
                              <th className="px-3 py-3 text-sm font-semibold text-muted-foreground">{tx("Summary output")}</th>
                              <th className="px-3 py-3 text-sm font-semibold text-muted-foreground">{tx("Inbound")}</th>
                              <th className="px-3 py-3 text-sm font-semibold text-muted-foreground">{tx("Outbound")}</th>
                              <th className="px-3 py-3 text-sm font-semibold text-muted-foreground">{tx("Branding")}</th>
                              <th className="px-3 py-3 text-center text-sm font-semibold text-muted-foreground">
                                <span className="sr-only">{tx("Remove")}</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {preferences.chatRules.map((rule, index) => (
                              <tr
                                key={rule.chatId}
                                className={index === preferences.chatRules.length - 1 ? "" : "border-b border-border"}
                              >
                                <td className="px-4 py-4 align-top">
                                  <ChatRuleName chatId={rule.chatId} chatsById={chatsById} />
                                </td>
                                <td className="px-3 py-4 align-top">
                                  <ChatRuleSelect
                                    value={rule.targetLanguageCode || "__none__"}
                                    options={chatLanguageOptions}
                                    onValueChange={(value) => updateChatRule(
                                      setPreferences,
                                      rule.chatId,
                                      "targetLanguageCode",
                                      normalizeChatLanguageSelectValue(value),
                                    )}
                                    ariaLabel={`${tx("Target language for")} ${rule.chatId}`}
                                  />
                                </td>
                                <td className="px-3 py-4 align-top">
                                  <ChatRuleSelect
                                    value={rule.deliveryLanguageMode || "auto"}
                                    options={DELIVERY_LANGUAGE_OPTIONS}
                                    onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "deliveryLanguageMode", value)}
                                    ariaLabel={`${tx("Delivery language mode for")} ${rule.chatId}`}
                                  />
                                </td>
                                <td className="px-3 py-4 align-top">
                                  <ChatRuleSelect
                                    value={rule.inboundMode}
                                    options={CHAT_RULE_OPTIONS}
                                    onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "inboundMode", value)}
                                    ariaLabel={`${tx("Inbound mode for")} ${rule.chatId}`}
                                  />
                                </td>
                                <td className="px-3 py-4 align-top">
                                  <ChatRuleSelect
                                    value={rule.outboundMode}
                                    options={CHAT_RULE_OPTIONS}
                                    onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "outboundMode", value)}
                                    ariaLabel={`${tx("Outbound mode for")} ${rule.chatId}`}
                                  />
                                </td>
                                <td className="px-3 py-4 align-top">
                                  <ChatRuleSelect
                                    value={rule.brandingMode}
                                    options={BRANDING_OPTIONS}
                                    onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "brandingMode", value)}
                                    ariaLabel={`${tx("Branding mode for")} ${rule.chatId}`}
                                  />
                                </td>
                                <td className="px-3 py-4 align-top">
                                  <div className="flex justify-center">
                                    <RemoveChatRuleButton
                                      chatId={rule.chatId}
                                      onRemove={() => removeChatRule(setPreferences, rule.chatId)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="space-y-3 md:hidden">
                        {preferences.chatRules.map((rule) => (
                          <div key={rule.chatId} className="rounded-[1.5rem] border border-border p-4">
                            <div className="flex items-start justify-between gap-3">
                              <ChatRuleName chatId={rule.chatId} chatsById={chatsById} />
                              <RemoveChatRuleButton
                                chatId={rule.chatId}
                                onRemove={() => removeChatRule(setPreferences, rule.chatId)}
                                className="h-9 w-9 rounded-xl text-muted-foreground"
                              />
                            </div>
                            <div className="mt-4 grid gap-3">
                              <div className="space-y-1">
                                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{tx("Language")}</div>
                                <ChatRuleSelect
                                  value={rule.targetLanguageCode || "__none__"}
                                  options={chatLanguageOptions}
                                  onValueChange={(value) => updateChatRule(
                                    setPreferences,
                                    rule.chatId,
                                    "targetLanguageCode",
                                    normalizeChatLanguageSelectValue(value),
                                  )}
                                  ariaLabel={`${tx("Target language for")} ${rule.chatId}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{tx("Summary output")}</div>
                                <ChatRuleSelect
                                  value={rule.deliveryLanguageMode || "auto"}
                                  options={DELIVERY_LANGUAGE_OPTIONS}
                                  onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "deliveryLanguageMode", value)}
                                  ariaLabel={`${tx("Delivery language mode for")} ${rule.chatId}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{tx("Inbound")}</div>
                                <ChatRuleSelect
                                  value={rule.inboundMode}
                                  options={CHAT_RULE_OPTIONS}
                                  onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "inboundMode", value)}
                                  ariaLabel={`${tx("Inbound mode for")} ${rule.chatId}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{tx("Outbound")}</div>
                                <ChatRuleSelect
                                  value={rule.outboundMode}
                                  options={CHAT_RULE_OPTIONS}
                                  onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "outboundMode", value)}
                                  ariaLabel={`${tx("Outbound mode for")} ${rule.chatId}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{tx("Branding")}</div>
                                <ChatRuleSelect
                                  value={rule.brandingMode}
                                  options={BRANDING_OPTIONS}
                                  onValueChange={(value) => updateChatRule(setPreferences, rule.chatId, "brandingMode", value)}
                                  ariaLabel={`${tx("Branding mode for")} ${rule.chatId}`}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {chatsError ? (
                  <div className="mt-4 rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
                    {tx(chatsError)}
                  </div>
                ) : null}
              </section>
            </div>
          ) : null}
        </div>
      </div>

      <StickyBar
        label={canSave ? tx("Save preferences") : tx(`Upgrade to ${getPlanLabel(requiredPlan)}`)}
        loading={isPending}
        loadingLabel={canSave ? tx("Saving…") : tx("Continuing…")}
        disabled={isPending}
        visible={showStickyBar}
        onClick={() => void handlePrimaryAction()}
        className={canSave ? "" : "bg-emerald-600 text-white shadow-lg shadow-emerald-900/20 hover:bg-emerald-500"}
        containerClassName="md:px-6"
        desktopVisible
      />
    </div>
  );
}
